import React from "react";

const HighlightText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-semibold text-[#4949DE]">{children}</span>
);

const RefundAndCancel = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header with gradient bar */}
      
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Returns, Refunds & Cancellation Policy
          </h1>
        </div>

        {/* Introduction */}
        <div className="mb-8 text-sm leading-relaxed  space-y-2">
          <p className="text-sm text-[black] font-semibold underline">Last Updated: 13 Dec 2025</p>
          <p>
            SOOMUCH SMART SOLUTIONS PRIVATE LIMITED (“we”, “our”, “us”) provides digital software products and subscription-based services through the platform Soomuch.ai. This policy is designed to comply with requirements of payment gateway providers and Indian e-commerce regulations.
          </p>
        </div>

        {/* Section 1: Returns */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">1. Returns</h2>
          <p className="text-sm leading-relaxed  mb-2">
            Our products and services are <HighlightText>digital</HighlightText> and delivered electronically. <br />
            Therefore, <HighlightText>returns are not applicable</HighlightText> once the service has been activated or access has been granted.
          </p>
        </section>

        {/* Section 2: Refund Policy */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">2. Refund Policy</h2>
          <p className="text-sm leading-relaxed  mb-4">
            Refunds may be granted only under the following conditions:
          </p>

          <div className="space-y-5">
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">2.1 Service Not Provided</h3>
              <p className="text-sm leading-relaxed ">
                If the purchased service is <HighlightText>not delivered, not activated, or not accessible</HighlightText> beyond the expected delivery timeline due to an error from our side.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">2.2 Duplicate Payment</h3>
              <p className="text-sm leading-relaxed ">
                If the customer makes a duplicate/accidental payment for the same order or invoice.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">2.3 Technical or System Failure</h3>
              <p className="text-sm leading-relaxed ">
                If there is a <HighlightText>verified technical failure</HighlightText> on our end that prevents usage of the subscribed service, and the issue cannot be resolved within a reasonable time frame.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">2.1 Service Not Provided</h3>
              <p className="text-sm leading-relaxed ">
                If the purchased service is <HighlightText>not delivered, not activated, or not accessible</HighlightText> beyond the expected delivery timeline due to an error from our side.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">2.1 Cancellation Before Activation</h3>
              <p className="text-sm leading-relaxed ">
                If the customer requests cancellation <HighlightText>before</HighlightText> the service is activated, a full or partial refund may be issued after review.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">2.5 Non-Refundable Cases</h3>
              <p className="text-sm leading-relaxed  mb-3">
                Refunds will not be provided under the following circumstances:
              </p>
              <ul className="text-sm leading-relaxed  space-y-1.5 pl-8">
                <li className="list-disc">Services already activated, used or accessed.</li>
                <li className="list-disc">Customer changes their decision after purchase.</li>
                <li className="list-disc">Issues arising from customer-side (Internet/Connectivity/Device Problems)</li>
                <li className="list-disc">Misuse or Incorrect use of the platform by the customer.</li>
                <li className="list-disc">Failure to Utilize the service during the subscription period.</li>
                <li className="list-disc">Requests made after the subscription tenure has expired.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">2.6 Refund Processing Time</h3>
              <p className="text-sm leading-relaxed ">
                Once Approved, refunds are processed within <HighlightText>7-14 working days</HighlightText> through the original payment method. Bank processing timelines may vary.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Cancellation Policy */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">3. Cancellation Policy</h2>
          
          <div className="space-y-5">
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">3.1 Subscription Cancellation</h3>
              <p className="text-sm leading-relaxed ">
                Customers may cancel their subscription at any time, but cancellation does not automatically qualify for a refund unless it meets refund criteria.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">3.2 Auto-Renewal</h3>
              <p className="text-sm leading-relaxed  mb-2">
                If your service includes auto-renewal:
              </p>
              <ul className="text-sm leading-relaxed  space-y-1.5 pl-8">
                <li className="list-disc">You may request cancellation for future billing cycles</li>
                <li className="list-disc">Refunds for auto-renewal will not be issued once the billing cycle has started or the service has been used.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">3.3 Cancellation of School/Institutional Plans</h3>
              <p className="text-sm leading-relaxed  mb-2">
                For institution-level subscription packages:
              </p>
              <ul className="text-sm leading-relaxed  space-y-1.5 pl-8">
                <li className="list-disc">Cancellations must be submitted in writing by the authorized representative</li>
                <li className="list-disc">Prorated refunds are not applicable unless agreed upon in the service agreement.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 4: How to Request */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">4. How to Request a Refund or Cancellation</h2>
          <p className="text-sm leading-relaxed  mb-3">
            Customers may raise a request through:
          </p>
          <p className="text-sm leading-relaxed  mb-3">
            <HighlightText>Email:</HighlightText> support@soomuch.ai
          </p>
          <p className="text-sm leading-relaxed  mb-2 font-semibold">
            Please include:
          </p>
          <ul className="text-sm leading-relaxed  space-y-1.5 pl-8 mb-3">
            <li className="list-disc">Full Name</li>
            <li className="list-disc">Institution Name & Address</li>
            <li className="list-disc">Registered Email/Mobile Number</li>
            <li className="list-disc">Order/Transaction ID</li>
            <li className="list-disc">Reason for refund/cancellation</li>
          </ul>
          <p className="text-sm leading-relaxed ">
            Our team will respond within <HighlightText>48-72 working hours</HighlightText>.
          </p>
        </section>

        {/* Section 5: Terms and Conditions */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">5. Terms and Conditions</h2>
          <p className="text-sm leading-relaxed  mb-4">
            The following terms and conditions apply to <HighlightText>all users of soomuch.ai</HighlightText>
          </p>

          <div className="space-y-5">
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">5.1 Acceptance of Terms</h3>
              <p className="text-sm leading-relaxed ">
                By accessing or using our website, applications, or services, you agree to comply with these Terms and Conditions. If you do not agree, please discontinue usage.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">5.2 Service Nature</h3>
              <p className="text-sm leading-relaxed  mb-2">Soomuch.ai provides:</p>
              <ul className="text-sm leading-relaxed  space-y-1.5 pl-8 mb-2">
                <li className="list-disc">Digital SaaS services for schools and institutions</li>
                <li className="list-disc">AI-powered communication systems</li>
                <li className="list-disc">Fee management tools</li>
                <li className="list-disc">Parent and student portals</li>
                <li className="list-disc">CRM modules</li>
                <li className="list-disc">Digital Marketing and related services</li>
              </ul>
              <p className="text-sm leading-relaxed ">
                These services are <HighlightText>delivered electronically</HighlightText> and do not include any physical goods.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">5.3 Pricing and Payments</h3>
              <ul className="text-sm leading-relaxed  space-y-1.5 pl-8">
                <li className="list-disc">All prices are listed in INR unless otherwise stated</li>
                <li className="list-disc">We reserve the right to modify pricing at any time</li>
                <li className="list-disc">Payment once made for digital services is <HighlightText>final</HighlightText>, except in cases eligible for a refund</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">5.4 User Responsibilities</h3>
              <p className="text-sm leading-relaxed  mb-2">Users shall:</p>
              <ul className="text-sm leading-relaxed  space-y-1.5 pl-8 mb-2">
                <li className="list-disc">Provide accurate information during registration</li>
                <li className="list-disc">Not misuse or illegally distribute our software</li>
                <li className="list-disc">Not attempt to reverse-engineer, duplicate or resell the platform.</li>
              </ul>
              <p className="text-sm leading-relaxed ">
                Failure to follow these may result in suspension or termination of access & legal action.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">5.5 Intellectual Property</h3>
              <p className="text-sm leading-relaxed ">
                All content, design, software code, branding, and materials available on Soomuch.ai are the intellectual property of <HighlightText>SOOMUCH SMART SOLUTIONS PRIVATE LIMITED</HighlightText>. Unauthorized reproduction is prohibited.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">5.6 Limitation of Liability</h3>
              <p className="text-sm leading-relaxed  mb-2">We are not responsible for:</p>
              <ul className="text-sm leading-relaxed  space-y-1.5 pl-8 mb-2">
                <li className="list-disc">Losses arising from user-side network/device issues</li>
                <li className="list-disc">Unauthorized access caused by the user sharing login credentials</li>
                <li className="list-disc">Any indirect, indirect, incidental, or consequential damages</li>
              </ul>
              <p className="text-sm leading-relaxed ">
                Our maximum liability, under any circumstance, shall not exceed the amount paid by the customer for the service.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">5.7 Service Interruptions</h3>
              <p className="text-sm leading-relaxed ">
                We may temporarily suspend services for maintenance, upgrades or technical issues. <br />We will make reasonable efforts to notify users in advance.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">5.8 Termination of Service</h3>
              <p className="text-sm leading-relaxed  mb-2">We may suspend or terminate access for:</p>
              <ul className="text-sm leading-relaxed  space-y-1.5 pl-8">
                <li className="list-disc">Violation of terms</li>
                <li className="list-disc">Misuse or malicious activities</li>
                <li className="list-disc">Non-payment of subscription fees</li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">5.9 Governing Law</h3>
              <p className="text-sm leading-relaxed ">
                This policy and our Terms are governed by the laws of <HighlightText>India</HighlightText>, with jurisdiction in <HighlightText>Hyderabad, Telangana</HighlightText>.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: Policy Updates */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">6. Policy Updates</h2>
          <p className="text-sm leading-relaxed ">
            We may update this policy from time to time. Changes will be posted on this page, and continued usage after updates constitutes acceptance.
          </p>
        </section>
      </div>

      {/* Footer gradient bar */}
      <div className="bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 h-2 w-full mt-12" />
    </div>
  );
};

export default RefundAndCancel;